import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import RoleNavigation from '../../components/ui/RoleNavigation';
import Icon from '../../components/AppIcon';
import useRealtimeSubscription from '../../hooks/useRealtimeSubscription';
import ApplicationTimeline from './components/ApplicationTimeline';
import ApplicationSummaryCard from './components/ApplicationSummaryCard';
import StatusDetailPanel from './components/StatusDetailPanel';
import DocumentStatusIndicators from './components/DocumentStatusIndicators';
import ContactSupportSection from './components/ContactSupportSection';
import ReapplyCTA from './components/ReapplyCTA';
import StatusToast from './components/StatusToast';

const CardApplicationStatusScreen = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();

  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toastMessage, setToastMessage] = useState('');

  const loadApplication = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchError } = await supabase
        ?.from('card_applications')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(1)
        ?.maybeSingle();

      if (fetchError) throw fetchError;
      setApplication(data || null);
    } catch (err) {
      console.error('[CardStatus] loadApplication error:', err);
      setError(err?.message || 'Failed to load application data.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadApplication();
    window.scrollTo(0, 0);
  }, [loadApplication]);

  // Real-time subscription on card_applications filtered by user_id
  useRealtimeSubscription({
    channelName: `card-application-status-${user?.id}`,
    table: 'card_applications',
    event: 'UPDATE',
    filter: user?.id ? `user_id=eq.${user?.id}` : undefined,
    enabled: !!user?.id,
    onUpdate: useCallback((payload) => {
      const updated = payload?.new;
      if (!updated) return;
      setApplication((prev) => {
        if (!prev || prev?.id !== updated?.id) return prev;
        const oldStatus = prev?.status;
        const newStatus = updated?.status;
        if (oldStatus !== newStatus) {
          const statusLabels = {
            under_review: 'Your application is now Under Review',
            eligibility: 'Eligibility Check has started',
            approved: '🎉 Your application has been Approved!',
            rejected: 'Application decision has been made',
          };
          setToastMessage(statusLabels?.[newStatus] || `Status updated to: ${newStatus}`);
        }
        return { ...prev, ...updated };
      });
    }, []),
  });

  const statusLabel = {
    pending: { text: 'Submitted', color: '#60a5fa', bg: 'rgba(96,165,250,0.12)', border: 'rgba(96,165,250,0.3)' },
    submitted: { text: 'Submitted', color: '#60a5fa', bg: 'rgba(96,165,250,0.12)', border: 'rgba(96,165,250,0.3)' },
    under_review: { text: 'Under Review', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)' },
    eligibility: { text: 'Eligibility Check', color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)', border: 'rgba(139,92,246,0.3)' },
    approved: { text: 'Approved', color: '#10b981', bg: 'rgba(16,185,129,0.12)', border: 'rgba(16,185,129,0.3)' },
    rejected: { text: 'Not Approved', color: '#ef4444', bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.3)' },
  }?.[application?.status] || { text: 'Submitted', color: '#60a5fa', bg: 'rgba(96,165,250,0.12)', border: 'rgba(96,165,250,0.3)' };

  return (
    <>
      <Helmet>
        <title>Application Status — SocialCents Card</title>
      </Helmet>
      <RoleNavigation role={userProfile?.role || 'influencer'} userName={userProfile?.full_name || user?.email} />
      <StatusToast message={toastMessage} onClose={() => setToastMessage('')} />
      <div className="min-h-screen pt-[60px]" style={{ background: '#0F172A' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">

          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/social-cents-card-screen')}
              className="flex items-center gap-2 mb-5 text-sm transition-all hover:opacity-80"
              style={{ color: 'rgba(255,255,255,0.45)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              <Icon name="ArrowLeft" size={15} />
              Back to Card Page
            </button>

            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg,#0d9488,#1d4ed8)' }}
                >
                  <Icon name="CreditCard" size={18} color="#fff" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Application Status</h1>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>SocialCents Card Application</p>
                </div>
              </div>

              {application && (
                <div
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
                  style={{ background: statusLabel?.bg, border: `1px solid ${statusLabel?.border}` }}
                >
                  <div
                    className="w-1.5 h-1.5 rounded-full"
                    style={{
                      background: statusLabel?.color,
                      animation: ['pending', 'submitted', 'under_review', 'eligibility']?.includes(application?.status)
                        ? 'pulse 2s infinite' :'none',
                    }}
                  />
                  <span style={{ fontSize: '12px', color: statusLabel?.color, fontWeight: 600 }}>{statusLabel?.text}</span>
                </div>
              )}
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(20,184,166,0.1)', border: '1px solid rgba(20,184,166,0.2)' }}
              >
                <Icon name="Loader2" size={22} color="#14B8A6" className="animate-spin" />
              </div>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>Loading your application...</p>
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div
              className="flex items-start gap-3 p-5 rounded-2xl mb-6"
              style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}
            >
              <Icon name="AlertCircle" size={18} color="#ef4444" className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold" style={{ color: '#ef4444' }}>Failed to load application</p>
                <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.45)' }}>{error}</p>
                <button
                  onClick={loadApplication}
                  className="mt-3 text-xs font-semibold transition-all hover:opacity-80"
                  style={{ color: '#14B8A6', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  Try again →
                </button>
              </div>
            </div>
          )}

          {/* No application found */}
          {!loading && !error && !application && (
            <div
              className="flex flex-col items-center justify-center py-20 text-center"
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: '16px',
              }}
            >
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
                style={{ background: 'rgba(255,255,255,0.05)' }}
              >
                <Icon name="FileSearch" size={28} color="rgba(255,255,255,0.25)" />
              </div>
              <h3 className="text-base font-semibold mb-2" style={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'DM Sans, sans-serif' }}>No Application Found</h3>
              <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.35)', maxWidth: '280px' }}>
                You haven't submitted a card application yet. Start your application to get started.
              </p>
              <button
                onClick={() => navigate('/card-application-form-screen')}
                className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
                style={{ background: 'linear-gradient(135deg,#0d9488,#1d4ed8)' }}
              >
                <Icon name="FilePlus" size={15} />
                Apply for Card
              </button>
            </div>
          )}

          {/* Application content */}
          {!loading && !error && application && (
            <div className="space-y-5">
              {/* Timeline + Summary side by side on large screens */}
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
                <div className="lg:col-span-3">
                  <ApplicationTimeline application={application} />
                </div>
                <div className="lg:col-span-2">
                  <ApplicationSummaryCard application={application} />
                </div>
              </div>

              {/* Status Detail */}
              <StatusDetailPanel application={application} />

              {/* Document Status */}
              <DocumentStatusIndicators application={application} />

              {/* Re-apply CTA (only for rejected) */}
              <ReapplyCTA application={application} />

              {/* Contact Support */}
              <ContactSupportSection application={application} userEmail={user?.email} />

              {/* Email notification reminder */}
              <div
                className="flex items-start gap-3 p-4 rounded-xl"
                style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.18)' }}
              >
                <Icon name="Bell" size={14} color="#f59e0b" className="flex-shrink-0 mt-0.5" />
                <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.45)', lineHeight: '1.6' }}>
                  You'll receive email notifications at each stage. Estimated total review time:{' '}
                  <strong style={{ color: 'rgba(255,255,255,0.7)' }}>3–5 business days</strong>.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CardApplicationStatusScreen;
